/**
 * WHO IS SIGNED IN, AND WHICH SHOP THEY BELONG TO.
 *
 * Two separate questions, and the old system kept confusing them. An auth
 * user is a login. A profile is that login's place in a shop — the
 * account it belongs to and what it is allowed to do. Somebody can have
 * the first without the second, and when they do, every screen is empty:
 * row level security keys on `profiles.account_id`, and there is nothing
 * to key on.
 *
 * That state is not an error and it is not a bug. It is a person who has
 * signed up but has not been let in yet, and it deserves its own screen
 * saying so rather than an empty table that looks like a broken query.
 */

import { db, api, publicSite, loadConfig, API_BASE } from "./client.ts";
import type { Session } from "@supabase/supabase-js";

export type Profile = {
  id: string;
  account_id: string;
  full_name: string;
  email: string | null;
  role: "admin" | "supervisor" | "advisor" | "technician" | "viewer";
};

export type Account = {
  id: string;
  name: string;
  labour_rate: number;
  /**
   * What this shop works on, which decides the words every screen uses --
   * vehicle or door, VIN or serial number. It rides along here because
   * whoAmI() is the one place the account is already loaded, so no screen
   * has to fetch it. See src/domain/trade.ts; null reads as 'auto', which
   * is every shop before the trade migration.
   */
  trade: string | null;
  /**
   * Which zone this shop's DAY is read in -- when the schedule rolls over,
   * what "today" means on the Desk tally. Rides along for the same reason
   * trade does: whoAmI() already has the account open, and src/ui/shopclock.ts
   * is primed from it at boot so no render function has to await a lookup.
   *
   * `accounts.timezone` is `not null default 'America/Los_Angeles'` since
   * migration 0001, so this is never actually null in the database. Typed
   * nullable anyway, because a select that a policy trims is indistinguishable
   * from one that came back complete, and usableZone() answers Pacific for
   * both.
   */
  timezone: string | null;
  /**
   * The shop's own texting line, or null for a shop that has none yet.
   *
   * Anthony, 2026-09-29, on Swift: "add the ability for them to answer their
   * customers in comms like a normal pro account." The comms panel's reply
   * box is drawn for any shop with a line of its own. A shop without one
   * keeps the read-only ledger, because a reply from a number that is not
   * theirs lands on somebody else's front desk. The server checks again
   * before anything is sent; this only decides what is drawn.
   */
  sms_number: string | null;
  /**
   * The shop's own desk host (shop.wrenchservices.com), or null. Customer
   * links are built on it -- see customerSite() in client.ts.
   */
  domain: string | null;
  /**
   * 'ppi' for the PrePurchase Auto desk, which runs the inspection board
   * instead of quotes and invoices (src/domain/ppidesk.ts, migration 0212).
   * Missing until 0212 is applied, which reads as 'repair'.
   */
  desk_mode?: string | null;
};

export type Who =
  | { state: "signed-out" }
  /** Signed in, but not attached to any shop. */
  | { state: "no-profile"; session: Session }
  | { state: "in"; session: Session; profile: Profile; account: Account };

export async function whoAmI(): Promise<Who> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  if (!session) return { state: "signed-out" };

  // One round trip for both. The profile carries the account, and the
  // account carries the shop's labor rate, which pricing needs everywhere.
  const { data, error } = await sb
    .from("profiles")
    .select("id,account_id,full_name,email,role,accounts(id,name,labour_rate,trade,timezone,sms_number,domain)")
    .eq("id", session.user.id)
    .maybeSingle();

  if (error || !data) return { state: "no-profile", session };

  const { accounts, ...profile } = data as unknown as Profile & { accounts: Account | null };
  if (!accounts) return { state: "no-profile", session };
  /* desk_mode on its own, and allowed to fail. Asked for in the select
     above, a database without migration 0212 would refuse the whole row
     and sign every shop out; asked here, it can only ever read as repair. */
  if (accounts.domain && /prepurchaseauto/i.test(accounts.domain)) {
    const { data: m } = await sb.from("accounts").select("desk_mode").eq("id", accounts.id).maybeSingle();
    accounts.desk_mode = (m as { desk_mode?: string } | null)?.desk_mode ?? "repair";
  }
  return { state: "in", session, profile: profile as Profile, account: accounts };
}

export async function signIn(email: string, password: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.auth.signInWithPassword({ email, password });
  if (error) throw new Error(friendly(error.message));
}

/**
 * SIGN IN WITH GOOGLE.
 *
 * Anthony, 2026-10-21: "Do we have login with Google on our shop desk
 * now? we should. test it on Supercanic version first."
 *
 * The answer comes back in the URL fragment as #access_token=..., the same
 * shape a password link uses, so tookAPasswordLink() in main.ts already
 * catches it before the router can throw it away. Supabase links a Google
 * login to the existing user with the same verified email, so a shop's
 * staff land in their own profile, not an empty one.
 */
export async function signInWithGoogle(): Promise<void> {
  const sb = await db();
  const { error } = await sb.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: publicSite() + "/" },
  });
  if (error) throw new Error(friendly(error.message));
}

/**
 * Whether the button may be drawn at all. Asked of the auth server rather
 * than hard coded, so the button cannot appear before the provider is
 * switched on in Supabase (a dead button that errors is worse than none),
 * and nothing has to be redeployed the day it is.
 *
 * Never inside the app store shell: Google sends the answer back to a web
 * address, and the shell lives at capacitor://localhost, which a browser
 * cannot return to.
 */
let googleOn: Promise<boolean> | null = null;
export function googleSignInAvailable(): Promise<boolean> {
  if (API_BASE) return Promise.resolve(false);
  googleOn ??= (async () => {
    try {
      const c = await loadConfig();
      const r = await fetch(c.supabaseUrl.replace(/\/$/, "") + "/auth/v1/settings",
        { headers: { apikey: c.supabaseAnonKey } });
      if (!r.ok) return false;
      const d = await r.json() as { external?: { google?: boolean } };
      return d.external?.google === true;
    } catch { return false; }
  })();
  return googleOn;
}

export async function signUp(email: string, password: string): Promise<{ needsConfirmation: boolean }> {
  const sb = await db();
  const { data, error } = await sb.auth.signUp({ email, password });
  if (error) throw new Error(friendly(error.message));
  // No session back means the project is set to confirm addresses by email.
  return { needsConfirmation: !data.session };
}

/**
 * SEND SOMEBODY A LINK TO CHOOSE A NEW PASSWORD.
 *
 * The approval email has always told people to "use Forgot password on the
 * sign-in page". There was no such thing on the sign-in page. Matthew Bolen
 * spent four days locked out behind that sentence: his one-time link had
 * been used, and the instruction for what to do next pointed at a button
 * that did not exist.
 *
 * main.ts already catches `type=recovery` in the URL and shows a
 * pick-a-password screen, so the only missing piece was the thing that
 * sends the mail.
 */
export async function sendReset(email: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.auth.resetPasswordForEmail(email.trim(), {
    /* The shell's origin is capacitor://localhost, and a reset email
       pointing there opens nothing. See publicSite(). */
    redirectTo: publicSite() + "/",
  });
  if (error) throw new Error(friendly(error.message));
}

/**
 * SET A NEW PASSWORD WHILE ALREADY SIGNED IN.
 *
 * Somebody who got in through a magic link has a live session but no
 * password they know, and sendReset above is the long way round -- sign
 * out, find Forgot Password, wait for mail. This writes the new password
 * straight onto the signed-in user, no email round trip, so the profile
 * page can offer it in one tap. Supabase enforces the minimum length and
 * friendly() turns its answer into a sentence.
 */
export async function setMyPassword(password: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.auth.updateUser({ password });
  if (error) throw new Error(friendly(error.message));
}

export async function signOut(): Promise<void> {
  const sb = await db();
  await sb.auth.signOut();
}

/**
 * Attach this login to the seeded test shop. Staging only — the server
 * refuses on anything else, and refuses to alter a profile that exists.
 */
export async function joinTestShop(role = "admin"): Promise<void> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  if (!session) throw new Error("Sign in first.");
  const res = await fetch(api("/api/provision"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ role }),
  });
  const body = (await res.json()) as { error?: string };
  if (!res.ok) throw new Error(body.error ?? "That did not work.");
}

/**
 * Supabase's messages are accurate and unkind. These are the three a
 * person actually hits, said the way a person would say them.
 */
function friendly(msg: string): string {
  if (/invalid login credentials/i.test(msg)) return "That email and password do not match.";
  if (/email not confirmed/i.test(msg)) return "Check your email and confirm the address first.";
  if (/at least 6 characters/i.test(msg)) return "The password needs at least six characters.";
  if (/already registered/i.test(msg)) return "There is already an account on that address — sign in instead.";
  return msg;
}

/**
 * THE OTHER DESKS THIS PERSON WORKS ON — see server/routes/desks.ts.
 * Empty for a login with one desk, and empty when the server cannot say,
 * because a switcher that fails should vanish rather than break the bar.
 */
export type OtherDesk = { profileId: string | null; name: string; host: string; role: string | null;
  current: boolean; oneTap: boolean };

export async function myDesks(): Promise<OtherDesk[]> {
  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return [];
    const r = await fetch(api("/api/desks"), { headers: { Authorization: `Bearer ${session.access_token}` } });
    if (!r.ok) return [];
    return ((await r.json()) as { desks?: OtherDesk[] }).desks ?? [];
  } catch { return []; }
}

/** Where to send the browser to land on that desk, signed in when the server can do that. */
export async function deskUrl(d: OtherDesk): Promise<string> {
  const plain = `https://${d.host}/index.html`;
  if (!d.profileId) return plain;
  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return plain;
    const r = await fetch(api("/api/desks/open"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ profileId: d.profileId }),
    });
    const b = (await r.json().catch(() => ({}))) as { url?: string };
    return r.ok && b.url ? b.url : plain;
  } catch { return plain; }
}
